import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';

import boldUnselect from '../assets/icons/chatIcons/boldUnselect.svg';
import italicUnselect from '../assets/icons/chatIcons/italicUnselect.svg';
import underlineUnselect from '../assets/icons/chatIcons/underlineUnselect.svg';
import strikeUnselect from '../assets/icons/chatIcons/strikeUnselect.svg';
import listUnselect from '../assets/icons/chatIcons/listUnselect.svg';
import bulletUnselect from '../assets/icons/chatIcons/bulletUnselect.svg';
import boldSelect from '../assets/icons/chatIcons/boldSelect.svg';
import italicSelect from '../assets/icons/chatIcons/italicSelect.svg';
import underlineSelect from '../assets/icons/chatIcons/underlineSelect.svg';
import strikeSelect from '../assets/icons/chatIcons/strikeSelect.svg';
import listSelect from '../assets/icons/chatIcons/listSelect.svg';
import bulletSelect from '../assets/icons/chatIcons/bulletSelect.svg';
import sendDefault from '../assets/icons/chatIcons/sendDefault.svg';
import sendButton from '../assets/icons/chatIcons/sendButton.svg';
import { v4 as uuidv4 } from 'uuid';

import 'react-quill/dist/quill.snow.css';
import './ChatBox.css';
import EverbaryChatMessages from './EverbaryChatMessages';
import { auth, fieldValue, firestore } from '../firebase.config';
import Tooltip from './shared/tooltip';

const EverbaryChatBox = ({type="order", userId, userRole, orderChatId, orderData, userData }) => {
  const [messages, setMessages] = useState([]);
  const [value, setValue] = useState('');
  const [sendIcon, setSendIcon] = useState(sendDefault);
 const [loading, setLoading] = useState(true);
  const [activeList, setActiveList] = useState([])
  const [chatID, setChatID] = useState(orderChatId ?? null);
  const [isActive, setIsActive] = useState(false);
  const messagesEndRef = useRef(null);


  const quillRef = useRef(null);

  useEffect(() => {

  let authUnsub;
    const init = async () => {
      authUnsub = auth.onAuthStateChanged(async (user) => {
        try {
          if (!user || !orderData?.id) {
            setLoading(false);
            return;
          }
          const chatQuery = await firestore
            .collection('everbarychats')
            .where('itemID', '==', orderData.id)
            .get();
          if (!chatQuery.empty) {
            const chatDoc = chatQuery.docs[0];
            setChatID(chatDoc.id);
            const chatData = chatDoc.data();
            const readMessages = (chatData.messages || []).map(msg =>
              msg.senderId !== userId ? { ...msg, unRead: false } : msg
            );
            await chatDoc.ref.update({ messages: readMessages });
          } else {
            setLoading(false);
          }
        } catch (error) {
        console.log(error);
        setLoading(false);
      }
    });

    };
    init();
    return () => authUnsub && authUnsub();
  }, [orderData?.id, userId]);

  useEffect(() => {
    if (!chatID) return;
  
  
    const unsubscribe = firestore
      .collection('everbarychats')
      .doc(chatID)
      .onSnapshot(
        async (doc) => {
          if (!doc.exists) return;
          const chatData = doc.data();
            const messages = chatData.messages || [];
  
            const readMessages = messages.map(msg => msg.senderId !== userId ? { ...msg, unRead: false } : msg);
            if (JSON.stringify(readMessages) !== JSON.stringify(messages)) {
              await doc.ref.update({ messages: readMessages });
            }
            const senderIds = [...new Set(readMessages.map((msg) => msg.senderId))];
            const userDetailsSnapshots = await Promise.all(
              senderIds.map((senderId) => firestore.collection('users').doc(senderId).get())
            );
            const userDetails = userDetailsSnapshots.reduce((acc, snap) => {
              acc[snap.id] = snap.data();
              return acc;
            }, {});
            const messagesWithUserDetails = readMessages.map((msg) => ({
              ...msg,
              senderName: userDetails[msg.senderId]?.name || "",
              senderRole: userDetails[msg.senderId]?.role || "",
              profilePic: userDetails[msg.senderId]?.profilePic || "",
            }));
  
            setMessages(messagesWithUserDetails);
            scrollToBottom();
          setLoading(false);
        },
        (error) => {
          console.error("Error in snapshot listener:", error);
          setLoading(false);
        }
      );
  
    return () => unsubscribe();
  }, [chatID, userId]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();

    if (quill) {
      quill.on('editor-change', (eventName) => {
        if (eventName === 'selection-change') {
          const currentFormats = quill.getFormat(); 
          const updatedActiveList = Object.keys(currentFormats); 
         
          if (updatedActiveList.includes('ordered') && updatedActiveList.includes('bullet')) {
            updatedActiveList.splice(updatedActiveList.indexOf('bullet'), 1);
          }

          setActiveList(updatedActiveList);
        }
      });
    }
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleOption = (option) => {
    const quill = quillRef.current?.getEditor();
    const currentContent = quill.getText().trim();

    if (currentContent === '') {
      quill.setText('');
    }

    let updatedList = [...activeList];

    if (option === 'ordered' || option === 'bullet') {
      updatedList = updatedList.filter((item) => item !== 'ordered' && item !== 'bullet');
      if (!activeList.includes(option)) {
        updatedList.push(option);
      }
      setActiveList(updatedList);
      quill.format('list', activeList.includes(option) ? false : option);
    } else {
      if (activeList.includes(option)) {
        setActiveList(updatedList.filter((item) => item !== option));
        quill.format(option, false);
      } else {
        updatedList.push(option);
        setActiveList(updatedList);
        quill.format(option, true);
      }
    }
  };



  const handleSend = async () => {
    const strippedContent = value.replace(/<p><br><\/p>/g, '').trim();

    if (strippedContent === '' || strippedContent === '<br>') return; 

    try {
      if (!chatID) {
        if (!orderData.id || !userId || !value) {
          console.error("Missing required fields for creating chat");
          return;
        }

        const newChatRef = firestore.collection('everbarychats').doc();
        const newChatData = {
          itemID: orderData.id,
          createdAt: fieldValue.serverTimestamp(),
          messages: [
            {
              messageID: uuidv4(),
              senderId: userId,
              message: strippedContent, // Send only the cleaned message
              timestamp: new Date().getTime(),
              unRead: true,
            },
          ],
        };

        await newChatRef.set(newChatData).then(() => firestore.collection(type === "order" ? "orders" : "quotes").doc(orderData.id).update({ everbaryChatId: newChatRef.id }));
        
        setChatID(newChatRef.id);
        
      } else {
        const chatRef = firestore.collection('everbarychats').doc(chatID);

        await chatRef.update({
          messages: fieldValue.arrayUnion({
              messageID: uuidv4(), 
              senderId: userId,
              message: strippedContent, // Save only the cleaned message
              timestamp: new Date().getTime(),
              unRead: true,
          }),
        });
      }

      const quill = quillRef.current.getEditor();
      quill.setText('');
      setValue('');
      setSendIcon(sendDefault);

      setActiveList([])
      scrollToBottom();

    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !(e.shiftKey)) {
      e.preventDefault();
      const strippedContent = value.replace(/<p><br><\/p>/g, '').trim();
      if (strippedContent !== '' && strippedContent !== '<br>') {
        handleSend();
      }
    }
  };

  const handleEditorChange = (content) => {
    const strippedContent = content.replace(/<p><br><\/p>/g, '').trim();
    const isEmpty = strippedContent.length === 0 || strippedContent === '<br>';
    setValue(isEmpty ? '' : content);
    setIsActive(!isEmpty);
    setSendIcon(isEmpty ? sendDefault : sendButton);
  };

  const CustomToolbar = () => (
    <div className="custom-toolbar ql-toolbar ql-snow">
      <div className='relative'>
        <button className={`toolbar-button ${activeList.includes('bold') ? 'active' : ''}`} onClick={() => toggleOption('bold')}>
          <img src={activeList.includes('bold') ? boldSelect : boldUnselect} alt="Bold" />
        </button>
        <Tooltip text='Bold' letter='B' />
      </div>
      <div className='relative'>
        <button className={`toolbar-button ${activeList.includes('italic') ? 'active' : ''}`} onClick={() => toggleOption('italic')}>
          <img src={activeList.includes('italic') ? italicSelect : italicUnselect} alt="Italic" />
        </button>
        <Tooltip text='Italic' letter='I' />
      </div>
      <div className='relative'>
        <button className={`toolbar-button ${activeList.includes('underline') ? 'active' : ''}`} onClick={() => toggleOption('underline')}>
          <img src={activeList.includes('underline') ? underlineSelect : underlineUnselect} alt="Underline" />
        </button>
        <Tooltip text='Underline' letter='U' />
      </div>
      <div className='relative'>
        <button className={`toolbar-button ${activeList.includes('strike') ? 'active' : ''}`} onClick={() => toggleOption('strike')}>
          <img src={activeList.includes('strike') ? strikeSelect : strikeUnselect} alt="Strike" />
        </button>
        <Tooltip text="Strikethrough" letter="Shift + X" width='120'/>
      </div>
      <div className='relative'>
        <button className={`toolbar-button ${activeList.includes('ordered') ? 'active' : ''}`} onClick={() => toggleOption('ordered')}>
          <img src={activeList.includes('ordered') ? listSelect : listUnselect} alt="Ordered List" />
        </button>
        <Tooltip text="Numbered List" letter="Shift + 7" width='120'/>
      </div>
      <div className='relative'>
        <button className={`toolbar-button ${activeList.includes('bullet') ? 'active' : ''}`} onClick={() => toggleOption('bullet')}>
          <img src={activeList.includes('bullet') ? bulletSelect : bulletUnselect} alt="Bullet List" />
        </button>
        <Tooltip text="Bulleted List" letter="Shift + 8" width='120'/>
      </div>
    </div>
  );

  const modules = {
    toolbar: {
      container: '.custom-toolbar',
    },
  };

  const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet'];

  return (
    <div className="chatDisplay">
      {loading ? (
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            ) : !messages.length ? (
              <div className="noMessageContainer">
                <h3>You Don't Have Messages Yet</h3>
                <p>Send your first</p>
              </div>
            ) : (
              <EverbaryChatMessages messages={messages} userRole={userRole} userId={userId}/>
            )}


      <div className={`chatDisplayContainer ${isActive ? 'active-chat-border' : ''}`}>
        <CustomToolbar />
        <div className="editor-container">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={handleEditorChange}
            modules={modules}
            formats={formats}
            placeholder="Message"
            onKeyDown={handleKeyPress}
          />
          <button className="send-button" onClick={handleSend}>
            <img src={sendIcon} alt="Send" className="send-icon" />
          </button>
        </div>
      </div>
    </div>
  );

};

export default EverbaryChatBox;
